/* eslint-disable @typescript-eslint/no-unused-vars */
import UserIcon from '@mui/icons-material/AccountCircle';
import HomeIcon from '@mui/icons-material/Home';
import LogoutIcon from '@mui/icons-material/Logout';
import { Divider, Popper } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Modal from '@mui/material/Modal';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useCurrentTenant, UserInfo } from '@qpr/client-library';
import React, { MouseEvent, useState } from 'react';
import { useLocation } from 'react-router-dom';
import UserModal from './UserModal';

const classes = {
  menuTextRow: 'UserMenu-menuTextRow',
  menuItem: 'UserMenu-menuItem',
  menuIcon: 'UserMenu-menuIcon'
};

const Root = styled('div')(({ theme }) => ({
  [`& .${classes.menuTextRow}`]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2)
  },
  [`& .${classes.menuItem}`]: {
    '&:hover': {
      background: theme.palette.primary.light
    },
    width: '256px',
    padding: theme.spacing(2)
  },
  [`& .${classes.menuIcon}`]: {
    width: '44px',
    paddingRight: theme.spacing(3),
    fill: theme.palette.primary.main
  }
}));

export interface UserMenuProps {
  userInfo: UserInfo;
  handleLogOut: () => void;
  handleLandingUrl: (profileId?: string, landingUrl?: string, landingTitle?: string | null) => void;
}

export default function UserMenu({
  userInfo,
  handleLogOut,
  handleLandingUrl
}: UserMenuProps): JSX.Element {
  const [modalOpen, setModalOpen] = useState(false);
  const { pathname, search } = useLocation();
  const tenant = useCurrentTenant().symbol;
  const [anchorMenu, setAnchorMenu] = useState<null | HTMLElement>(null);
  const [anchorConfirm, setAnchorConfirm] = useState<null | HTMLElement>(null);
  let timeoutConfirm: number;
  const [landingPage, setLandingPage] = useState({
    url: userInfo.landingUrl,
    title: userInfo.landingTitle
  });

  function handleModal(): void {
    setAnchorMenu(null);
    setModalOpen(true);
  }

  function parsePageTitle(): string | null | undefined {
    return (
      document.querySelector("[data-testid*='page-header']") ||
      document.querySelector("[data-testid*='page-title']") ||
      [...document.querySelectorAll('h2')].find(element => !!element.textContent) ||
      [...document.querySelectorAll('h5')].find(element => !!element.textContent) ||
      [...document.querySelectorAll('h6')].find(element => !!element.textContent)
    )?.textContent;
  }

  function setLandingUrl(event: React.MouseEvent<HTMLElement>): void {
    const url = `${pathname.substring(pathname.indexOf(tenant) + tenant.length)}${search}`;
    const title = parsePageTitle() || url;
    handleLandingUrl(userInfo.profileId, url, title);
    if (timeoutConfirm) clearTimeout(timeoutConfirm);
    setAnchorConfirm(event.currentTarget);
    function updateLandingUrl(): void {
      setAnchorConfirm(null);
      //  set new values
      setLandingPage({ url, title });
    }
    timeoutConfirm = window.setTimeout(updateLandingUrl, 5000);
  }

  function dismissLandingUrl(): void {
    if (timeoutConfirm) clearTimeout(timeoutConfirm);
    setAnchorConfirm(null);
    //  return old values
    handleLandingUrl(userInfo.profileId, landingPage.url, landingPage.title);
  }

  return (
    <>
      <IconButton
        color="inherit"
        aria-controls="simple-menu"
        aria-haspopup="true"
        aria-label="iconbutton4"
        onClick={(event: MouseEvent<HTMLButtonElement>) =>
          setAnchorMenu(anchorMenu || modalOpen ? null : event.currentTarget)
        }
        data-testid="user-menu-button"
        size="large"
      >
        <UserIcon />
      </IconButton>
      <Menu
        id="simple-menu"
        anchorEl={anchorMenu}
        anchorReference="anchorPosition"
        anchorPosition={{ top: 72, left: window.innerWidth }}
        keepMounted
        open={Boolean(anchorMenu)}
        onClick={() => setAnchorMenu(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        data-testid="app-user-menu"
      >
        <Root>
          <Typography variant="h6" className={classes.menuTextRow}>
            {userInfo.fullname}
          </Typography>
          <Typography
            className={classes.menuTextRow}
            variant="body2"
            data-testid="appbar-user-caption"
            sx={{ paddingBottom: '10px' }}
          >
            {userInfo.email}
          </Typography>
          <Divider />
          <MenuItem
            className={classes.menuItem}
            onClick={handleModal}
            data-testid="appbar-button-profile"
          >
            <UserIcon className={classes.menuIcon} />
            <Typography variant="body2">Profile</Typography>
          </MenuItem>
          <MenuItem
            className={classes.menuItem}
            onClick={setLandingUrl}
            data-testid="appbar-button-landingurl"
          >
            <HomeIcon className={classes.menuIcon} />
            <Typography variant="body2">Set as home page</Typography>
          </MenuItem>
          <MenuItem
            className={classes.menuItem}
            onClick={handleLogOut}
            data-testid="appbar-button-logout"
          >
            <LogoutIcon className={classes.menuIcon} />
            <Typography variant="body2">Log out</Typography>
          </MenuItem>
        </Root>
      </Menu>
      <Modal
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      >
        <UserModal userInfo={userInfo} handleModalClose={() => setModalOpen(false)} />
      </Modal>
      <Popper
        // onResize={() => {}}
        // onResizeCapture={() => {}}
        open={Boolean(anchorConfirm)}
        anchorEl={anchorConfirm}
      >
        <Typography
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '15px',
            background: 'rgb(96, 107, 123)',
            borderRadius: '4px',
            textAlign: 'center',
            verticalAlign: 'middle',
            width: '360px',
            marginTop: `${window.innerHeight - 380}px`,
            marginRight: `${(window.innerWidth - 360) / 2}px`
          }}
        >
          <Typography
            display="inline"
            sx={{
              fontFamily: 'OpenSans-Regular',
              fontSize: '14px',
              fontWeight: 'normal',
              letterSpacing: '0.25px',
              lineHeight: '20px',
              color: 'rgb(255, 255, 255)'
            }}
          >
            Home page set
          </Typography>
          <Typography
            display="inline"
            sx={{
              fontFamily: 'OpenSans-SemiBold',
              fontSize: '14px',
              fontWeight: '600',
              letterSpacing: '1.25px',
              lineHeight: '20px',
              color: 'rgb(128, 212, 255)',
              cursor: 'pointer'
            }}
            onClick={dismissLandingUrl}
            data-testid="dismiss-landing-url"
          >
            DISMISS
          </Typography>
        </Typography>
      </Popper>
    </>
  );
}
